// @ts-check
import { formatDate, moduleTypeToLabel } from '@/lib/utils';
import {
  REVIEW_CHANGE_BADGE_CONFLICT,
  REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT,
  REVIEW_CHANGE_BADGE_CONFIG,
} from '@/lib/constants/manuscripts/store';
import {
  proseMirrorToHtml,
  hasProseMirrorText,
  flattenParagraphRuns,
  renderMarkedText,
  renderNebulaIcon,
  decodeHtmlEntities,
} from './proseMirrorHtml';

/**
 * @typedef {import('./studioDiffTypes').DriftChange} DriftChange
 * @typedef {import('./studioDiffTypes').DriftResponse} DriftResponse
 * @typedef {import('./studioDiffTypes').DriftSide} DriftSide
 * @typedef {import('./studioDiffTypes').TextDiffParagraph} TextDiffParagraph
 * @typedef {import('./studioDiffTypes').ReviewChangeSlot} ReviewChangeSlot
 * @typedef {import('./studioDiffTypes').ReviewChangeCardItem} ReviewChangeCardItem
 */

const CONTENT_NOT_PREVIEWABLE = 'This content type isn\'t previewable yet.';

// A badge the backend sends that isn't in this config is dropped rather than guessed at.
const KNOWN_BADGES = new Set(Object.keys(REVIEW_CHANGE_BADGE_CONFIG));

/** @param {DriftSide | null | undefined} side */
function sideTime(side) {
  return side?.updated_on ? formatDate(side.updated_on, true) : null;
}

// manuscript's own Page.number vs. Studio's 1-indexed sort_index rank -- can differ after a reorder.
/** @param {DriftSide | null | undefined} side */
function slideLabel(side) {
  return Number.isFinite(side?.order) ? `Slide ${side.order}` : null;
}

/**
 * @typedef {{ text: string; highlight?: string | null }} ParagraphRun
 */

/** @param {ParagraphRun[][]} paragraphs */
function hasText(paragraphs) {
  return paragraphs.some((paragraph) => paragraph.some((segment) => segment.text));
}

// Studio has no explicit "teacher note" field on a module -- newModuleTemplate() in Studio's
// own store.js hardcodes layout.width = 1 only for notes-canvas modules, so a synced note is
// the only text module that ever reports a width of exactly 1.
/** @param {DriftChange} item */
function isTeacherNote(item) {
  return item.studio?.content?.layout?.width === 1;
}

// A whole paragraph added/removed outright has no segments -- synthesize one from old/new.
/** @param {TextDiffParagraph} paragraph */
function paragraphSegments(paragraph) {
  if (paragraph.segments) return paragraph.segments;
  if (paragraph.op === 'insert') return paragraph.new ? [{ op: 'insert', text: paragraph.new }] : [];
  if (paragraph.op === 'delete') return paragraph.old ? [{ op: 'delete', text: paragraph.old }] : [];
  return [
    ...(paragraph.old ? [{ op: 'delete', text: paragraph.old }] : []),
    ...(paragraph.new ? [{ op: 'insert', text: paragraph.new }] : []),
  ];
}

// An 'equal' paragraph has identical old/new text -- either works as the plain text.
/** @param {TextDiffParagraph} paragraph */
function paragraphText(paragraph) {
  return paragraph.new ?? paragraph.old ?? '';
}

// Splits `runs` (from flattenParagraphRuns) at the char offsets `segments` implies.
// Only `consumingOp` (+'equal') consumes runs -- the other op never existed in this
// doc, so it's emitted as plain highlighted text. Zero-width icon/break runs flush the
// moment the cursor reaches them, even mid-segment, instead of being skipped.
function alignRunsWithSegments(runs, segments, highlightByOp, consumingOp) {
  let runIdx = 0;
  let offsetInRun = 0;
  const consume = (len) => {
    const pieces = [];
    let remaining = len;
    while (runIdx < runs.length) {
      const run = runs[runIdx];
      if (run.icon || run.break) {
        pieces.push(run.icon ? { icon: run.icon } : { break: true });
        runIdx += 1;
      } else if (remaining <= 0) {
        break;
      } else {
        const take = Math.min(run.text.length - offsetInRun, remaining);
        if (take > 0) pieces.push({ text: run.text.substr(offsetInRun, take), marks: run.marks });
        offsetInRun += take;
        remaining -= take;
        if (offsetInRun >= run.text.length) {
          runIdx += 1;
          offsetInRun = 0;
        }
      }
    }
    return pieces;
  };

  return segments.flatMap((segment) => {
    const highlight = highlightByOp[segment.op] ?? null;
    if (segment.op !== 'equal' && segment.op !== consumingOp) {
      return segment.text ? [{ text: segment.text, marks: [], highlight }] : [];
    }
    return consume(segment.text.length).map((piece) => ({ ...piece, highlight }));
  });
}

function highlightWrap(html, highlight) {
  if (!highlight) return html;
  return `<span class="review-change-rich-text__highlight review-change-rich-text__highlight--${highlight}">${html}</span>`;
}

function renderAlignedRun(run) {
  if (run.icon) return renderNebulaIcon(run.icon);
  if (run.break) return '<br>';
  return renderMarkedText(run.text, run.marks);
}

// text_diff's insert/delete are always manuscript -> studio, so a 'delete' entry has no
// counterpart in studio's real doc, and an 'insert' entry has no counterpart in manuscript's.
function existsOnSide(paragraph, side) {
  return side === 'studio' ? paragraph.op !== 'delete' : paragraph.op !== 'insert';
}

// proseMirrorToHtml `renderParagraph` override: splices text_diff highlighting onto
// paragraph[index]'s real runs (index = doc order, filtered to entries that exist on
// editedSide -- a raw index would desync past any one-sided paragraph); 'equal' renders as-is.
function buildRenderParagraph(paragraphs, editedSide, highlightByOp) {
  const consumingOp = editedSide === 'studio' ? 'insert' : 'delete';
  const sideParagraphs = paragraphs.filter((paragraph) => existsOnSide(paragraph, editedSide));
  return (node, index) => {
    const diffEntry = sideParagraphs[index];
    if (!diffEntry || diffEntry.op === 'equal') return null;
    const runs = flattenParagraphRuns(node);
    const segments = paragraphSegments(diffEntry);
    const aligned = alignRunsWithSegments(runs, segments, highlightByOp, consumingOp);
    return aligned
      .map((run) => highlightWrap(renderAlignedRun(run), run.highlight))
      .join('');
  };
}

// A side's raw ProseMirror JSON (studio: options.content; manuscript: text when
// text_type is 'prosemirror'); null otherwise -- callers fall back to flat rendering.
/**
 * @param {DriftChange} item
 * @param {'studio' | 'manuscript'} sideKey
 */
function rawProseMirrorDoc(item, sideKey) {
  const side = item[sideKey];
  if (sideKey === 'studio') return side?.content?.options?.content ?? null;
  return side?.content?.text_type === 'prosemirror' ? (side?.content?.text ?? null) : null;
}

/** @param {string | null} raw */
function parsedProseMirrorDoc(raw) {
  if (!raw) return null;
  try {
    const doc = JSON.parse(raw);
    return hasProseMirrorText(doc) ? doc : null;
  } catch {
    return null;
  }
}

// Real doc + text_diff highlighting spliced in; null (no doc/bad JSON/no text) falls back to flat paragraphs.
/**
 * @param {DriftChange} item
 * @param {'studio' | 'manuscript'} editedSide
 * @param {TextDiffParagraph[]} paragraphs
 */
function richEditedHtml(item, editedSide, paragraphs, highlightByOp) {
  const doc = parsedProseMirrorDoc(rawProseMirrorDoc(item, editedSide));
  if (!doc) return null;
  return proseMirrorToHtml(doc, { renderParagraph: buildRenderParagraph(paragraphs, editedSide, highlightByOp) });
}

// The untouched side has nothing to diff -- render its own real doc as-is, no highlighting.
/**
 * @param {DriftChange} item
 * @param {'studio' | 'manuscript'} sideKey
 */
function richPlainHtml(item, sideKey) {
  const doc = parsedProseMirrorDoc(rawProseMirrorDoc(item, sideKey));
  return doc ? proseMirrorToHtml(doc) : null;
}

// Prefer real-doc HTML, then flat paragraphs, then the placeholder.
/**
 * @param {string | null} richHtml
 * @param {ParagraphRun[][]} flatParagraphs
 * @returns {Pick<ReviewChangeSlot, 'html' | 'paragraphs' | 'text' | 'notPreviewable'>}
 */
function contentSlotFields(richHtml, flatParagraphs) {
  if (richHtml) return { html: richHtml };
  if (hasText(flatParagraphs)) return { paragraphs: flatParagraphs };
  return { text: CONTENT_NOT_PREVIEWABLE, notPreviewable: true };
}

/**
 * @param {DriftSide | null | undefined} side
 * @param {string | null} richHtml
 * @param {ParagraphRun[][]} flatParagraphs
 * @returns {ReviewChangeSlot}
 */
function buildSlot(side, richHtml, flatParagraphs) {
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    ...contentSlotFields(richHtml, flatParagraphs),
  };
}

// text_diff's delete/insert are always manuscript -> studio; editedSide only flips which reads as added vs removed.
/**
 * @param {DriftChange} item
 * @param {'studio' | 'manuscript'} editedSide
 * @returns {{ manuscript: ReviewChangeSlot; studio: ReviewChangeSlot }}
 */
function buildEditedSides(item, editedSide) {
  const paragraphs = item.text_diff?.paragraphs ?? [];
  const highlightByOp = editedSide === 'manuscript'
    ? { delete: 'added', insert: 'removed' }
    : { delete: 'removed', insert: 'added' };
  const editedParagraphs = paragraphs.map((paragraph) => (paragraph.op === 'equal'
    ? [{ text: paragraphText(paragraph), highlight: null }]
    : paragraphSegments(paragraph).map((segment) => ({
      text: segment.text,
      highlight: highlightByOp[segment.op] ?? null,
    }))));
  const plainText = editedSide === 'manuscript' ? 'new' : 'old';
  // Flat fallback only: a paragraph new-only on the edited side renders blank here, but still positioned.
  const untouchedParagraphs = paragraphs.map((paragraph) => [{
    text: paragraph.op === 'equal' ? paragraphText(paragraph) : (paragraph[plainText] ?? ''),
    highlight: null,
  }]);

  const untouchedSide = editedSide === 'manuscript' ? 'studio' : 'manuscript';
  const editedSlot = buildSlot(item[editedSide], richEditedHtml(item, editedSide, paragraphs, highlightByOp), editedParagraphs);
  const untouchedSlot = buildSlot(item[untouchedSide], richPlainHtml(item, untouchedSide), untouchedParagraphs);
  return editedSide === 'manuscript'
    ? { manuscript: editedSlot, studio: untouchedSlot }
    : { studio: editedSlot, manuscript: untouchedSlot };
}

// Conflict variant of buildRenderParagraph: a side only shows segments that exist on it
// (equal + its own op) -- the other side's insert/delete has no real-doc counterpart, so
// it's dropped instead of falling back to a backend-only text block. Real marks (bold,
// highlight, etc.) on the kept segments render as-is from the side's own doc, so a
// mark-only divergence (e.g. one side highlighted a phrase) is visible without inventing
// a diff color for it.
function buildConflictRenderParagraph(paragraphs, side, keepOp, highlightValue) {
  const sideParagraphs = paragraphs.filter((paragraph) => existsOnSide(paragraph, side));
  const highlightByOp = { [keepOp]: highlightValue };
  return (node, index) => {
    const diffEntry = sideParagraphs[index];
    if (!diffEntry || diffEntry.op === 'equal') return null;
    const runs = flattenParagraphRuns(node);
    const segments = paragraphSegments(diffEntry).filter((segment) => segment.op === 'equal' || segment.op === keepOp);
    const aligned = alignRunsWithSegments(runs, segments, highlightByOp, keepOp);
    return aligned
      .map((run) => highlightWrap(renderAlignedRun(run), run.highlight))
      .join('');
  };
}

// Real doc + this side's own diff segments spliced in, for a conflict item; null falls back to flat paragraphs.
function richConflictHtml(item, side, paragraphs, keepOp, highlightValue) {
  const doc = parsedProseMirrorDoc(rawProseMirrorDoc(item, side));
  if (!doc) return null;
  return proseMirrorToHtml(doc, { renderParagraph: buildConflictRenderParagraph(paragraphs, side, keepOp, highlightValue) });
}

// Conflict: both sides diverged independently, so each shows its own real text (equal plain, its own segments highlighted).
/** @param {DriftChange} item */
function buildConflictSides(item) {
  const paragraphs = item.text_diff?.paragraphs ?? [];
  const manuscriptParagraphs = paragraphs.map((paragraph) => (paragraph.op === 'equal'
    ? [{ text: paragraphText(paragraph), highlight: null }]
    : paragraphSegments(paragraph)
      .filter((segment) => segment.op === 'equal' || segment.op === 'delete')
      .map((segment) => ({ text: segment.text, highlight: segment.op === 'delete' ? 'removed' : null }))));
  const studioParagraphs = paragraphs.map((paragraph) => (paragraph.op === 'equal'
    ? [{ text: paragraphText(paragraph), highlight: null }]
    : paragraphSegments(paragraph)
      .filter((segment) => segment.op === 'equal' || segment.op === 'insert')
      .map((segment) => ({ text: segment.text, highlight: segment.op === 'insert' ? 'added' : null }))));

  return {
    manuscript: buildSlot(
      item.manuscript,
      richConflictHtml(item, 'manuscript', paragraphs, 'delete', 'removed'),
      manuscriptParagraphs,
    ),
    studio: buildSlot(
      item.studio,
      richConflictHtml(item, 'studio', paragraphs, 'insert', 'added'),
      studioParagraphs,
    ),
  };
}

/** @param {DriftChange} item */
function buildDiffedSides(item) {
  if (item.badge === REVIEW_CHANGE_BADGE_CONFLICT) return buildConflictSides(item);
  return buildEditedSides(item, item.badge === REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT ? 'manuscript' : 'studio');
}

// manuscript side is a curated { target_url, external_url }; Studio side is
// the module's raw options doc, where a URL only exists for options.action === 'link'.
/**
 * @param {DriftSide} side
 * @param {boolean} isStudio
 */
function linkUrl(side, isStudio) {
  if (isStudio) {
    const options = side.content?.options ?? {};
    return options.action === 'link' ? (options.target ?? null) : null;
  }
  return side.content?.target_url || side.content?.external_url || null;
}

const CONTENT_TYPE_LABELS = {
  internal_asset: 'Asset',
  link: 'Link',
};

function humanize(value) {
  if (!value) return '';
  return value.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

// content_type is absent for module types the backend doesn't categorize (e.g. Shape) -- fall back to the raw module type instead of defaulting to Text.
/** @param {DriftChange} item */
function contentTypeLabel(item) {
  if (isTeacherNote(item)) return 'Teacher Note';
  if (item.content_type) return CONTENT_TYPE_LABELS[item.content_type] || humanize(item.content_type);
  const rawModuleType = item.studio?.content?.type ?? item.manuscript?.content?.type;
  return moduleTypeToLabel(rawModuleType) || 'Text';
}

/**
 * @param {string} contentType
 * @param {DriftSide | null | undefined} side
 */
function notPreviewableText(contentType, side) {
  const moduleType = moduleTypeToLabel(side?.content?.type);
  const detail = moduleType && moduleType !== contentType ? ` (${moduleType})` : '';
  return `${contentType} content${detail} isn't previewable here yet.`;
}

// These TEI types store options.content as a plain string, not JSON-stringified ProseMirror.
const PLAIN_TEXT_STUDIO_MODULE_TYPES = new Set(['prompt', 'single_selection', 'multiple_selection', 'block']);

// manuscript is a curated { text, text_type }; studio's text (if any) is ProseMirror
// JSON at options.content. Returns { text } for a plain string, { html } for ProseMirror.
/**
 * @param {DriftSide} side
 * @param {boolean} isStudio
 * @returns {Pick<ReviewChangeSlot, 'text' | 'html'> | null}
 */
function richTextFromContent(side, isStudio) {
  const raw = isStudio ? side.content?.options?.content : side.content?.text;
  if (!raw) return null;
  if (!isStudio && side.content?.text_type !== 'prosemirror') return { text: decodeHtmlEntities(raw) };
  if (isStudio && PLAIN_TEXT_STUDIO_MODULE_TYPES.has(side.content?.type)) return { text: raw };
  try {
    const doc = JSON.parse(raw);
    return hasProseMirrorText(doc) ? { html: proseMirrorToHtml(doc) } : null;
  } catch {
    return null;
  }
}

// assetLinkUrl mirrors the "Open Asset" link Studio's own MiniMenu builds.
// manuscript side is a curated { asset_id }; Studio side nests it under options,
// same as an asset module's own moduleDefaults shape (options.asset_id).
/**
 * @param {DriftSide} side
 * @param {boolean} isStudio
 * @param {string | undefined} appDomain
 * @param {string} contentType
 * @returns {ReviewChangeSlot}
 */
function buildNoDiffSide(side, isStudio, appDomain, contentType) {
  const assetId = isStudio ? side.content?.options?.asset_id : side.content?.asset_id;
  const url = linkUrl(side, isStudio);
  const richText = !assetId && !url ? richTextFromContent(side, isStudio) : null;
  // Manuscript has no shape concept -- only the raw Studio module content carries one.
  const shapeModuleData = (!assetId && !url && !richText && isStudio && side.content?.type === 'shape')
    ? side.content : null;
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    ...(assetId && { assetId, ...(appDomain && { assetLinkUrl: `${appDomain}/player/${assetId}` }) }),
    ...(url && { linkUrl: url }),
    ...richText,
    ...(shapeModuleData && { shapeModuleData }),
    ...(!assetId && !url && !richText && !shapeModuleData
      && { text: notPreviewableText(contentType, side), notPreviewable: true }),
  };
}

/**
 * @param {DriftChange} item
 * @param {string | undefined} appDomain
 */
function buildAddedSides(item, appDomain) {
  const contentType = contentTypeLabel(item);
  return {
    manuscript: item.manuscript ? buildNoDiffSide(item.manuscript, false, appDomain, contentType) : null,
    studio: item.studio ? buildNoDiffSide(item.studio, true, appDomain, contentType) : null,
  };
}

// STUDSYNC-472's orthogonal contract: `change` is one of modify/add/remove/reorder
// (collapsed from the old modified/add/unmatched/missing/removed/reorder six).
const CHANGE_LABELS = {
  modify: 'Modified',
  add: 'Added',
  remove: 'Removed',
};

/** @param {DriftChange} item */
function changeLabel(item) {
  return CHANGE_LABELS[item.change] || humanize(item.change);
}

/** @param {DriftChange} item */
function changeTypeLabel(item) {
  return [contentTypeLabel(item), changeLabel(item)].filter(Boolean).join(' ');
}

// A shape's backend label is always "Untitled" -- show its own type instead. Wording matches ShapeOptions.vue's shapeTypeOptions dropdown.
const SHAPE_TYPE_LABELS = {
  circle: 'Circle',
  box: 'Square',
  line: 'Line',
  arrow: 'Arrow',
  pentagon: 'Pentagon',
  rounded_rectangle: 'Rounded Rectangle',
  speech_bubble_lower_left: 'Speech Bubble Lower Left',
  speech_bubble_lower_right: 'Speech Bubble Lower Right',
  title_bubble: 'Title Bubble',
};

/** @param {string | null | undefined} rawShapeType */
function shapeTypeLabel(rawShapeType) {
  if (!rawShapeType) return 'Rectangle (Default)';
  return SHAPE_TYPE_LABELS[rawShapeType] || humanize(rawShapeType);
}

// No asset title anywhere in this response -- fetching one needs its own API call, so fall back to the id.
/** @param {DriftChange} item */
function itemAssetId(item) {
  return item.studio?.content?.options?.asset_id ?? item.manuscript?.content?.asset_id ?? null;
}

/** @param {DriftChange} item */
function itemLabel(item) {
  if (item.studio?.content?.type === 'shape') return shapeTypeLabel(item.studio.content.options?.shape);
  if (item.content_type === 'internal_asset' && (!item.label || item.label === 'Untitled')) {
    return itemAssetId(item) || item.label;
  }
  return item.label;
}

/** @param {DriftChange} item */
function cardTitle(item) {
  const primary = item.section?.title || itemLabel(item) || 'Untitled';
  const changeType = changeTypeLabel(item);
  const showLabel = !!(item.section?.title && item.label);
  const parts = [primary];
  if (changeType) parts.push(showLabel ? `${changeType}: ${itemLabel(item)}` : changeType);
  return parts.join(' | ');
}

/** @param {DriftChange} item */
function navLabel(item) {
  const primary = item.section?.title || itemLabel(item) || 'Untitled';
  const changeType = changeTypeLabel(item);
  const parts = [primary];
  if (changeType) parts.push(changeType);
  if (item.page?.order != null) parts.push(`Page: ${item.page.order}`);
  return parts.join(' | ');
}

// A row's own badge already names the situation ("Manuscript only") for the
// *_only case; can_sync:false on an otherwise-decidable badge needs a suffix
// since the plain badge text alone doesn't explain the gate. No new backend
// field -- both derive from `badge`/`can_sync`, which the endpoint already sends.
/** @param {DriftChange} item */
function nonDecidableReason(item) {
  const badgeText = REVIEW_CHANGE_BADGE_CONFIG[item.badge]?.text ?? '';
  if (!REVIEW_CHANGE_BADGE_CONFIG[item.badge]?.decidable) return badgeText;
  return `${badgeText} — can't sync yet`;
}

/** @param {DriftChange} item */
function isDecidable(item) {
  return !!item.can_sync && !!REVIEW_CHANGE_BADGE_CONFIG[item.badge]?.decidable;
}

/**
 * Maps the live GET /manuscripts/<id>/studio-diff response into what
 * ReviewChangeCard expects. Only a badge the panel recognizes is required to
 * keep a row -- a non-actionable row (can_sync: false, or a badge like
 * manuscript_only/studio_only that never gets a decide bar) is still kept and
 * rendered read-only via `decidable: false` + `reason`, not dropped.
 *
 * @param {DriftResponse | null | undefined} diff - the studio-diff response body.
 * @param {string} [appDomain] - `domains.appDomain` from the store, used to
 *   build an internal_asset's "Open Asset" link. Omit to skip that link.
 * @returns {ReviewChangeCardItem[]}
 */
export function mapStudioDiffToReviewChanges(diff, appDomain) {
  const changes = diff?.changes ?? [];
  return changes
    .filter((item) => KNOWN_BADGES.has(item.badge))
    .map((item) => {
      const sides = item.text_diff ? buildDiffedSides(item) : buildAddedSides(item, appDomain);
      const decidable = item.badge === REVIEW_CHANGE_BADGE_CONFLICT || isDecidable(item);
      return {
        id: item.id,
        title: cardTitle(item),
        navLabel: navLabel(item),
        badge: item.badge,
        isTeacherNote: isTeacherNote(item),
        decidable,
        ...(decidable ? {} : { reason: nonDecidableReason(item) }),
        ...sides,
      };
    });
}

export default mapStudioDiffToReviewChanges;
